import { createFilterOptions, type FilterOptionsState } from '@mui/material';
import { useCallback, useState } from 'react';

import {
  useCreateCompanyMutation,
  useLazyGetCompaniesQuery,
} from '@/features/contacts/companies/api/companiesApi';
import { AutocompleteAsync } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import type { CreateCompanyPayload } from '@/features/contacts/companies/types';
import type { Company } from '@/features/contacts/types';

type CreatedOption = {
  inputValue: string;
  label: string;
};

export type CompanyOption = {
  id: string;
  name: string;
};

type OptionType = CompanyOption | CreatedOption;

type Props = {
  value: CompanyOption | null;
  onChange: (value: CompanyOption | null) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
};

const filter = createFilterOptions<OptionType>();

const isCreatedOption = (option: unknown): option is CreatedOption =>
  typeof option === 'object' && option !== null && 'inputValue' in option;

const normalizeCompanies = (raw: unknown): Company[] => {
  if (Array.isArray(raw)) return raw as Company[];
  if (raw && typeof raw === 'object' && Array.isArray((raw as any).results)) {
    return (raw as any).results as Company[];
  }
  return [];
};

const getHasMore = (raw: unknown): boolean => {
  if (raw && typeof raw === 'object' && 'next' in raw) {
    return Boolean((raw as any).next);
  }
  return false;
};

export function CompanySelectField({
  value,
  onChange,
  label = 'Company',
  placeholder = 'Search companies...',
  error,
  disabled,
  size = 'small',
}: Props) {
  const [createCompany, { isLoading: isCreating }] = useCreateCompanyMutation();
  const [getCompanies] = useLazyGetCompaniesQuery();

  const [inputValue, setInputValue] = useState('');

  const loadOptions = useCallback(
    async ({ query, page }: { query: string; page: number }) => {
      const params: { page: number; query?: string } = { page };
      if (query) params.query = query;

      const res = await getCompanies(params).unwrap();
      const list = normalizeCompanies(res.data);
      const hasMore = getHasMore(res.data);
      return {
        options: list.map((company) => ({
          id: company.id,
          name: company.name,
        })),
        hasMore,
      };
    },
    [getCompanies],
  );

  const filterOptions = (
    optionList: OptionType[],
    state: FilterOptionsState<OptionType>,
  ): OptionType[] => {
    const filtered = filter(optionList, state);
    const trimmedInput = state.inputValue.trim();

    if (trimmedInput) {
      const hasExisting = optionList.some(
        (option) =>
          !isCreatedOption(option) &&
          option.name.toLowerCase() === trimmedInput.toLowerCase(),
      );

      if (!hasExisting) {
        filtered.push({
          inputValue: trimmedInput,
          label: `Add "${trimmedInput}"`,
        });
      }
    }

    return filtered;
  };

  const getOptionLabel = (option: OptionType) => {
    if (isCreatedOption(option)) return option.inputValue;
    return option.name;
  };

  const handleCreateCompany = async (name: string) => {
    const payload: CreateCompanyPayload = {
      avatar_url: '',
      name,
      description: '',
      website_url: '',
      phone_number: '',
      address_information: {
        country: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        zip_code: '',
      },
      social_links: [],
      tags: [], // * We'll keep this empty for now.
    };

    try {
      const res = await createCompany(payload);

      if ((res.data as any)?.success) {
        const created = (res.data as any).data as Company;
        await getCompanies({ page: 1, query: inputValue.trim() || undefined });
        onChange({ id: created.id, name: created.name });
        notify({
          type: 'success',
          message: `Company "${created.name}" created`,
        });
        return;
      }

      const apiMessage = (res as any)?.error?.data?.message;
      throw new Error(apiMessage ?? 'Failed to create company');
    } catch (err) {
      notify({ type: 'error', message: getErrorMessage(err) });
    }
  };

  const handleChange = (_event: unknown, nextValue: OptionType | null) => {
    if (!nextValue) {
      onChange(null);
      return;
    }

    if (isCreatedOption(nextValue)) {
      void handleCreateCompany(nextValue.inputValue);
      return;
    }

    onChange(nextValue);
  };

  return (
    <AutocompleteAsync<OptionType>
      value={value}
      loadOptions={loadOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, current) =>
        !isCreatedOption(option) && !isCreatedOption(current)
          ? option.id === current.id
          : false
      }
      filterOptions={filterOptions}
      inputValue={inputValue}
      onInputChange={(_event, nextValue) => setInputValue(nextValue)}
      onChange={handleChange}
      renderOption={(props, option) => {
        if (isCreatedOption(option)) {
          return <li {...props}>{option.label}</li>;
        }
        return <li {...props}>{option.name}</li>;
      }}
      noOptionsText="No companies"
      loadingText="Loading companies..."
      label={label}
      placeholder={placeholder}
      size={size}
      disabled={disabled || isCreating}
      textFieldProps={{
        error: Boolean(error),
        helperText: error,
      }}
    />
  );
}
